import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";

import { AppDialog, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { showToast } from "@/lib/utils/toast";
import { useAuthStore } from "@/features/auth";
import { useUserStore, useUserListDerived } from "../hooks";
import OrganizationSelect from "./OrganizationSelect";
import PermissionOverridesSection from "./PermissionOverridesSection";

const ADMIN_ROLES = ["system_admin", "business_unit"];

const USERNAME_ALLOWED_REGEX = /^[a-z0-9]+$/;

const SYSTEM_ROLE_OPTIONS = [
  { value: "system_admin", label: "System Admin" },
  { value: "business_unit", label: "Business Unit" },
  { value: "super_admin", label: "Super Admin" },
  { value: "reseller", label: "Reseller" },
  { value: "solution_provider", label: "Solution Provider" },
  { value: "bank_admin", label: "Bank Admin" },
  { value: "sme_user", label: "SME User" },
];

const CreateUserDialog = observer(({ open, onOpenChange }) => {
  const { t } = useTran();
  const { userRole } = useAuthStore();
  const {
    createForm,
    createLoading,
    createError,
    resetCreateForm,
    setCreateField,
    createManagedUser,
    setCreatePermissionOverrides,
    loadUsers,
  } = useUserStore();
  const {
    roleOptions,
    createPermissionModules,
    companyOptions,
    companyLoading,
    organizationTree,
    organizationLoading,
  } = useUserListDerived();

  const isCompanyAdmin = ADMIN_ROLES.includes(userRole);

  const resolvedRoleOptions = useMemo(() => {
    if (Array.isArray(roleOptions) && roleOptions.length > 0) {
      return roleOptions;
    }
    return SYSTEM_ROLE_OPTIONS.map((role) => ({
      ...role,
      label: t(role.label),
    }));
  }, [roleOptions, t]);

  const usernameValue = createForm.username || "";
  const usernameError =
    usernameValue && !USERNAME_ALLOWED_REGEX.test(usernameValue)
      ? t(
          "Username can only contain lowercase letters and numbers without spaces.",
        )
      : "";

  const requiresOrganization =
    !isCompanyAdmin && createForm.role !== "business_unit";
  const requiresCompany = isCompanyAdmin;
  const canSave =
    createForm.displayName &&
    createForm.username &&
    !usernameError &&
    createForm.email &&
    createForm.role &&
    (!requiresOrganization || createForm.organizationUnitId) &&
    (!requiresCompany || createForm.companyId) &&
    !createLoading;

  const handleDialogOpenChange = (nextOpen) => {
    if (!nextOpen) {
      resetCreateForm();
    }
    onOpenChange(nextOpen);
  };

  const handleChange = (field) => (event) => {
    setCreateField(field, event.target.value);
  };

  const handleSelect = (field) => (input) => {
    const value = input?.target ? input.target.value : input;
    if (field === "companyId") {
      setCreateField("companyId", value);
      setCreateField("organizationUnitId", "");
      return;
    }
    setCreateField(field, value);
  };

  const emptyCompanyLabel = t("No companies available");

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (createLoading) return;

    try {
      const payload = {
        displayName: createForm.displayName,
        username: createForm.username,
        email: createForm.email,
        role: createForm.role,
        organizationUnitId: isCompanyAdmin
          ? null
          : createForm.organizationUnitId,
        companyId: isCompanyAdmin ? createForm.companyId : undefined,
        permissionOverrides: createForm.permissionOverrides,
      };

      const createdUser = await createManagedUser(payload);
      loadUsers();
      if (createdUser?.temporaryPassword) {
        showToast.success(
          t("Temporary password: {{password}}", {
            password: createdUser.temporaryPassword,
          }),
        );
      }
      onOpenChange(false);
    } catch (error) {
      const message =
        error?.message || createError || t("Unable to create user.");
      showToast.error(message);
    }
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={handleDialogOpenChange}
      title={t("Create user")}
      description={t("Add a new user and set their basic details.")}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t("Display name")}
            </label>
            <Input
              value={createForm.displayName}
              onChange={handleChange("displayName")}
              placeholder={t("Enter display name")}
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t("Username")}
            </label>
            <Input
              value={createForm.username}
              onChange={handleChange("username")}
              placeholder={t("Enter username")}
              required
            />
            {usernameError ? (
              <p className="text-xs text-destructive">{usernameError}</p>
            ) : null}
          </div>
          {isCompanyAdmin ? (
            <SelectField
              id="companyId"
              label={t("Company Name")}
              value={createForm.companyId || undefined}
              onChange={handleSelect("companyId")}
              options={companyOptions}
              placeholder={t("Select a company")}
              disabled={companyLoading}
              emptyLabel={emptyCompanyLabel}
            />
          ) : (
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">
                {t("Organization")}
              </label>
              <OrganizationSelect
                value={createForm.organizationUnitId || undefined}
                onValueChange={handleSelect("organizationUnitId")}
                disabled={organizationLoading}
                placeholder={t("Select an organization")}
                tree={organizationTree}
              />
            </div>
          )}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              {t("Email")}
            </label>
            <Input
              type="email"
              value={createForm.email}
              onChange={handleChange("email")}
              placeholder={t("Enter email")}
              required
            />
          </div>
          <SelectField
            id="role"
            label={t("Role")}
            value={createForm.role || undefined}
            onChange={handleSelect("role")}
            options={resolvedRoleOptions}
            placeholder={t("Select a role")}
            emptyLabel={t("No roles available.")}
          />
        </div>

        <PermissionOverridesSection
          value={createForm.permissionOverrides}
          modules={createPermissionModules}
          onChange={setCreatePermissionOverrides}
        />

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleDialogOpenChange(false)}
            disabled={createLoading}
          >
            {t("Cancel")}
          </Button>
          <Button type="submit" disabled={!canSave}>
            {createLoading ? t("Saving...") : t("Save")}
          </Button>
        </div>
      </form>
    </AppDialog>
  );
});

export default CreateUserDialog;
